/**
 * GlobalErrorListener - Catches unhandled errors outside React rendering.
 *
 * Funnels every detected error through `crashDedupService.recordAndDecide` and
 * branches on the Decision: open the dialog, silent-send, or drop.
 */
import { useState, useEffect, useCallback, useRef } from 'react';
import { CrashDialog } from './CrashDialog';
import { collectCrashContextSync } from '../services/crashContextCollector';
import {
  recordAndDecide,
  recordStance,
  setDialogOpen,
  reset,
  shouldShowAutoSendToast,
  markAutoSendToastShown,
  buildReportEnrichment,
  signatureFor,
} from '../services/crashDedupService';
import { emitCrashTelemetry } from '../services/crashTelemetry';
import { isBenignCancellation, isMonacoBugIndicatingError } from '../services/monacoErrorSuppression';
import { syntheticError, toReportableError } from '../services/errorSerialization';
import type { CrashContext, CrashDialogAction, CrashReportEnrichment } from '../types/crashTypes';
import { useAppStore } from '../../../store/appStore';
import { toast } from '../../../components/ui/toaster';

/**
 * Chromium clipboard writes rejecting for lack of document focus, e.g.
 * `Failed to execute 'writeText' on 'Clipboard': Document is not focused.`
 * String-matched (not instanceof DOMException) so the synthetic errors the
 * `error` listener builds from message-only events match too.
 */
function isClipboardFocusError(error: unknown): boolean {
  const message =
    error instanceof Error
      ? error.message
      : typeof error === 'string'
        ? error
        : '';
  return message.includes("'Clipboard'") && message.includes('Document is not focused');
}

export function GlobalErrorListener() {
  const [crashContext, setCrashContext] = useState<CrashContext | null>(null);
  const currentSignatureRef = useRef<string | null>(null);

  const crashReportMode = useAppStore((s) => s.crashReportMode);
  const setCrashReportMode = useAppStore((s) => s.setCrashReportMode);

  const maybeShowAutoSendToast = useCallback(() => {
    if (!shouldShowAutoSendToast()) return;
    markAutoSendToastShown();
    toast.default({
      title: 'Crash report sent automatically',
      description: 'Change in Settings → Privacy.',
      duration: 6000,
    });
  }, []);

  // In E2E test mode, window error/unhandledrejection listeners are disabled by
  // default — the CrashDialog uses AlertDialog which can't be dismissed with
  // Escape, so an incidental error during an unrelated test would permanently
  // block pointer events. Tests that need to drive the listener (dedup E2E)
  // must opt in by setting `window.__TEST_CRASH_LISTENER_ENABLE__ = true`.
  const isTestMode = window.electronAPI?.testMode?.isTestMode?.() === true;

  const sendReport = useCallback(
    (context: CrashContext, enrichment?: CrashReportEnrichment) => {
      if (window.electronAPI?.crashDialog?.sendReport) {
        window.electronAPI.crashDialog.sendReport(context, enrichment).catch((err) => {
          if (import.meta.env.DEV) {
            console.error('Failed to send crash report:', err);
          }
        });
      }
    },
    [],
  );

  const handleError = useCallback((error: unknown) => {
    // Suppress Vite chunk-preload rejections (raw DOM Event, transient).
    if (typeof Event !== 'undefined' && error instanceof Event) {
      const target = error.target;
      const hint = target instanceof HTMLLinkElement ? target.href
        : target instanceof HTMLScriptElement ? target.src
        : '';
      console.warn(
        '[GlobalErrorListener] Suppressed chunk preload failure (Event rejection):',
        `type=${error.type}`,
        `constructor=${error.constructor?.name ?? 'unknown'}`,
        hint,
      );
      return;
    }

    // Benign async cancellations (monaco loader's plain-object rejection on
    // editor unmount-during-init, Monaco's own `Canceled` error) are by-design
    // control flow, not crashes — see monacoErrorSuppression.ts.
    if (isBenignCancellation(error)) {
      console.warn('[GlobalErrorListener] Suppressed benign cancellation rejection:', error);
      return;
    }

    // navigator.clipboard rejects with NotAllowedError "Document is not
    // focused" when the window loses focus before an async copy lands
    // (Chromium focus requirement). The copy failed but nothing crashed —
    // tell the user instead of filing a crash report. App copies now go
    // through the focus-free Electron bridge (src/lib/clipboard.ts), so this
    // is a back-stop for third-party writes and any missed call site.
    if (isClipboardFocusError(error)) {
      console.warn('[GlobalErrorListener] Suppressed unfocused-clipboard rejection:', error);
      toast.warning({
        title: 'Copy failed',
        description: 'The window lost focus before the copy finished. Click into the app and copy again.',
        duration: 5000,
      });
      return;
    }

    // Non-Error values (plain-object rejection reasons, strings) are converted
    // to a diagnosable Error with the synthesis frame suppressed — see
    // errorSerialization.ts for why `new Error(String(error))` is wrong here.
    const context = collectCrashContextSync(toReportableError(error), 'renderer');
    const decision = recordAndDecide(context, crashReportMode, 'global-listener');

    const errorName = error instanceof Error ? error.name : 'UnknownError';
    const dialogShown = decision.kind === 'show_dialog';
    const reportSent = decision.kind === 'silent_send';

    emitCrashTelemetry(
      (name, data) => window.electronAPI?.analytics?.track?.(name, data),
      {
        context,
        reportMode: crashReportMode,
        surface: 'renderer',
        source: 'global-listener',
        dialogShown,
        reportSent,
        errorName,
      },
    );

    if (decision.kind === 'show_dialog') {
      currentSignatureRef.current = signatureFor(context);
      setDialogOpen(true);
      setCrashContext(context);
      return;
    }
    if (decision.kind === 'silent_send') {
      sendReport(context, buildReportEnrichment(context, crashReportMode, 'silent'));
      if (crashReportMode === 'always') maybeShowAutoSendToast();
      return;
    }
    // silent_drop — no UI, no report.
  }, [crashReportMode, maybeShowAutoSendToast, sendReport]);

  useEffect(() => {
    if (isTestMode) {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      (window as any).__TEST_CRASH_DEDUP_RESET__ = () => reset();
    }

    // Test-mode protection: when `isTestMode` is true, the handlers bail out
    // unless the current test has explicitly opted in via
    // `window.__TEST_CRASH_LISTENER_ENABLE__ = true`. This preserves the
    // legacy guard — the CrashDialog uses AlertDialog which can't be
    // dismissed with Escape, so an incidental uncaught error in an unrelated
    // spec would permanently block pointer events for the rest of the worker
    // — while still letting dedup E2E tests drive the listener via synthetic
    // ErrorEvents.
    //
    // The check lives INSIDE each handler (not around addEventListener)
    // because `GlobalErrorListener` mounts once at app startup in the shared
    // worker fixture (`src/test/e2e/setup/appFixture.ts`), long before any
    // test's `beforeEach` can flip the flag. Re-evaluating the flag per
    // dispatch keeps the opt-in observable without re-running the effect.
    const shouldHandle = (): boolean =>
      !isTestMode ||
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      (window as any).__TEST_CRASH_LISTENER_ENABLE__ === true;

    const onError = (event: ErrorEvent) => {
      if (!shouldHandle()) return;

      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      if (event.error && (event.error as any).__reactErrorBoundary) return;

      // Benign browser warning surfaced as ErrorEvent — see W3C ResizeObserver
      // spec. Fired by Monaco's automaticLayout when its own relayout shifts
      // the container size again in the same observation cycle.
      if (typeof event.message === 'string' && event.message.includes('ResizeObserver loop')) return;

      // Monaco 0.44 fires `onUnexpectedError(new BugIndicatingError('Assertion
      // Failed'))` from its diff post-processing when internal invariants
      // drift. assertFn() does not throw — the diff still returns a valid
      // result — but the default Monaco error handler reschedules a
      // `setTimeout(throw)` that lands here. Suppress it (see
      // monacoErrorSuppression.ts for the full rationale).
      if (isMonacoBugIndicatingError(event.error)) return;

      console.error('[GlobalErrorListener] Uncaught error:', {
        message: event.message,
        filename: event.filename,
        lineno: event.lineno,
        colno: event.colno,
        error: event.error,
      });

      if (event.error instanceof Error) {
        handleError(event.error);
        return;
      }

      const baseMsg = String(event.message ?? 'Unknown error');
      const basename = event.filename ? event.filename.split(/[/\\]/).pop() || '' : '';
      const locPart = basename && event.lineno
        ? ` [${basename}:${event.lineno}:${event.colno ?? 0}]`
        : '';
      // syntheticError keeps the stack empty so the reported location stays
      // the event's own filename/lineno (already in the message), not this
      // synthesis site.
      handleError(syntheticError(`${baseMsg}${locPart}`));
    };

    const onUnhandledRejection = (event: PromiseRejectionEvent) => {
      if (!shouldHandle()) return;
      console.error('[GlobalErrorListener] Unhandled promise rejection:', event.reason);
      handleError(event.reason);
    };

    window.addEventListener('error', onError);
    window.addEventListener('unhandledrejection', onUnhandledRejection);

    return () => {
      window.removeEventListener('error', onError);
      window.removeEventListener('unhandledrejection', onUnhandledRejection);
      if (isTestMode) {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        delete (window as any).__TEST_CRASH_DEDUP_RESET__;
      }
    };
  }, [handleError, isTestMode]);

  const handleActionWithMemory = useCallback(
    (result: { action: CrashDialogAction; remember: boolean }) => {
      const signature = currentSignatureRef.current;
      if (result.action === 'send_report') {
        if (crashContext) sendReport(crashContext, buildReportEnrichment(crashContext, crashReportMode, 'dialog'));
        if (signature) recordStance(signature, 'sent');
        if (result.remember) setCrashReportMode('always');
      } else {
        if (signature) recordStance(signature, 'ignored');
        if (result.remember) setCrashReportMode('never');
      }
      currentSignatureRef.current = null;
      setDialogOpen(false);
      setCrashContext(null);
    },
    [crashContext, crashReportMode, sendReport, setCrashReportMode],
  );

  if (!crashContext) return null;

  return (
    <CrashDialog
      open={true}
      context={crashContext}
      onAction={() => { /* handled by onActionWithMemory */ }}
      onActionWithMemory={handleActionWithMemory}
    />
  );
}
